import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, HostBinding, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { TranslocoPipe } from '@jsverse/transloco';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { en_GB, en_US, NZ_I18N, NzI18nInterface, NzI18nService } from 'ng-zorro-antd/i18n';

import { UiIconsModule } from 'ui-icons';

import { UiPlatfomDatepickerModeType } from './models/datepicker.model';

type Value = unknown;

@Component({
  selector: 'ui-platform-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NzDatePickerModule,
    FormsModule,
    UiIconsModule,
    TranslocoPipe
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiPlatformDatepickerComponent),
      multi: true
    },
    { provide: NZ_I18N, useValue: en_US }
  ]
})
export class UiPlatformDatepickerComponent implements ControlValueAccessor, OnChanges {
  @Input() set locale(locale: NzI18nInterface) {
    this.i18n.setLocale(locale ?? en_GB);
  }
  @Input() disabled = false;
  @Input() mode: UiPlatfomDatepickerModeType = 'date';
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() disableDateFn?: (current: Date) => boolean;

  @HostBinding('class.inline') @Input() inline = false;
  // TODO: We HAVE TO figure out WHY was this implemented in some inputs and how to get rid of it
  @HostBinding('class.drawer') @Input() drawer = false;

  internalValue: Value = null;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly i18n: NzI18nService
  ) {
    this.i18n.setLocale(en_GB);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.placeholder?.currentValue && changes.mode?.currentValue) {
      this.placeholder = this.#getPlaceholderBasedOnMode();
    }
  }

  syncExternalValue: (value: Value) => void = () => {};
  syncExternalTouched: () => void = () => {};

  onModelChange(value: Value) {
    this.syncExternalValue(value);
    this.syncExternalTouched();
  }

  writeValue(value: Value) {
    this.internalValue = value;
    this.changeDetectorRef.detectChanges();
  }

  registerOnChange(fn: (value: Value) => void) {
    this.syncExternalValue = (value: Value) => {
      fn(value);
    };
  }

  registerOnTouched(fn: () => void) {
    this.syncExternalTouched = () => {
      fn();
    };
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.changeDetectorRef.detectChanges();
  }

  #getPlaceholderBasedOnMode(): string {
    switch (this.mode) {
      case 'year':
        return 'general.select_year';
      case 'month':
        return 'general.select_month';
      case 'week':
        return 'general.select_week';
      case 'date':
        return 'general.select_date';
      default:
        return 'general.select_date';
    }
  }
}
